import { reportingPrisma as prisma } from "client";
import { CHECK_IN_OUT } from "@sajilni/config";

const EMPTY_CHECKED_IN_OUT_SUMMARY = {
  CheckIn: 0,
  CheckOut: 0,
  peopleCheckedInCount: 0,
  peopleCheckedOutCount: 0,
  peopleCount: 0,
};

/**
 * Check-in/out summary for an event.
 * Starts from Order by eventId, projects ids only, and takes the latest
 * CheckInOut per item via sort+limit (uses orderItemId+time index).
 *
 * Empty success (no check-in rows) returns explicit zeros — never `[]`.
 * Callers must not treat a thrown error as zero.
 */
export const getCheckedInOut = async ({
  eventId,
  filters: _filters,
}: {
  eventId: string;
  filters: { [key: string]: string };
}) => {
  try {
    const stages = [
      {
        $match: {
          eventId: { $oid: eventId },
        },
      },
      { $project: { _id: 1 } },
      {
        $lookup: {
          from: "OrderItem",
          let: { orderId: "$_id" },
          pipeline: [
            {
              $match: {
                $expr: { $eq: ["$orderId", "$$orderId"] },
              },
            },
            {
              $lookup: {
                from: "CheckInOut",
                let: { itemId: "$_id" },
                pipeline: [
                  {
                    $match: {
                      $expr: { $eq: ["$orderItemId", "$$itemId"] },
                    },
                  },
                  { $sort: { time: -1 } },
                  { $limit: 1 },
                  { $project: { type: 1 } },
                ],
                as: "latestCheck",
              },
            },
            {
              $match: {
                "latestCheck.0": { $exists: true },
              },
            },
            {
              $project: {
                _id: 1,
                checksInOut: { $arrayElemAt: ["$latestCheck", 0] },
              },
            },
          ],
          as: "itemsWithLatestCheck",
        },
      },
      { $unwind: { path: "$itemsWithLatestCheck" } },
      {
        $group: {
          _id: null,
          total: { $sum: 1 },
          CheckIn: {
            $sum: {
              $cond: {
                if: {
                  $eq: [
                    "$itemsWithLatestCheck.checksInOut.type",
                    CHECK_IN_OUT.CHECK_IN,
                  ],
                },
                then: 1,
                else: 0,
              },
            },
          },
          CheckOut: {
            $sum: {
              $cond: {
                if: {
                  $eq: [
                    "$itemsWithLatestCheck.checksInOut.type",
                    CHECK_IN_OUT.CHECK_OUT,
                  ],
                },
                then: 1,
                else: 0,
              },
            },
          },
          people: { $addToSet: "$itemsWithLatestCheck._id" },
          peopleCheckedIn: {
            $addToSet: {
              $cond: {
                if: {
                  $eq: [
                    "$itemsWithLatestCheck.checksInOut.type",
                    CHECK_IN_OUT.CHECK_IN,
                  ],
                },
                then: "$itemsWithLatestCheck._id",
                else: null,
              },
            },
          },
          peopleCheckedOut: {
            $addToSet: {
              $cond: {
                if: {
                  $eq: [
                    "$itemsWithLatestCheck.checksInOut.type",
                    CHECK_IN_OUT.CHECK_OUT,
                  ],
                },
                then: "$itemsWithLatestCheck._id",
                else: null,
              },
            },
          },
        },
      },
      {
        $addFields: {
          peopleCheckedIn: {
            $filter: {
              input: "$peopleCheckedIn",
              as: "person",
              cond: { $ne: ["$$person", null] },
            },
          },
          peopleCheckedOut: {
            $filter: {
              input: "$peopleCheckedOut",
              as: "person",
              cond: { $ne: ["$$person", null] },
            },
          },
        },
      },
      {
        $project: {
          total: 1,
          CheckIn: 1,
          CheckOut: 1,
          peopleCount: { $size: "$people" },
          peopleCheckedInCount: { $size: "$peopleCheckedIn" },
          peopleCheckedOutCount: { $size: "$peopleCheckedOut" },
        },
      },
    ];
    const result = await prisma.order.aggregateRaw({
      pipeline: [...stages],
    });

    const row = result[0];
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      return { ...EMPTY_CHECKED_IN_OUT_SUMMARY };
    }
    return row;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
